import React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Chip, Button, IconButton, Grid, TextField, Stack, Popper, Grow, Paper, ClickAwayListener, Tabs, Tab,
    Menu, MenuItem, Collapse, Radio, RadioGroup, FormControlLabel,
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { BiListUl } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFilterAlt } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import CustomButton from "components/Button";
import { useLazyGetHSEListQuery, useGetHSEViewTypeListQuery } from "store/slices/apis/hseApiSlice";
import { useParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalenderSVG from "assets/logo/Calendar";
import { useMemo } from "react";
import AddHSE from "./AddHSE";
import HSEDataTable from "./HSEDataTable";
import HSEMonthViewDataTable from "./HSEMonthViewDataTable";
import { usePermission } from "hooks/usePermission";
import { HSE_CREATE } from "helpers/permissionConfig";
import { exportToExcel } from "./HSEExportToExcel";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { useDispatch } from "store";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { sectionOrder, transformPeriodData } from "./mapper";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import TrackerPageHeader from "components/TrackerPageHeader";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const formatPeriod = (value, viewType) => {
    if (!value) return "-";

    switch (viewType) {
        case "monthly":
            return dayjs(value, "MM-YYYY").format("MMM YYYY");

        case "daily":
            return dayjs(value, "DD-MM-YYYY").format("Do MMM YYYY");

        case "weekly": {
            const [start, end] = value.split(" to ");

            const startDate = dayjs(start, "DD-MM-YYYY").format("Do MMM");
            const endDate = dayjs(end, "DD-MM-YYYY").format("Do MMM");

            return `${startDate} to ${endDate}`;
        }

        default:
            return value;
    }
};

const hseSections = [
    { label: "Safe Manhours", value: "Safe Manhours" },
    { label: "Training", value: "Training" },
    { label: "Safety Meeting", value: "Safety Meeting" },
    { label: "Reward & Recognition", value: "Reward & Recognition" },
    { label: "Monitoring & Measurement", value: "Monitoring & Measurement" },
    { label: "Finding Tracker", value: "Finding Tracker" },
    { label: "Environment Reporting", value: "Environment Reporting" },
    { label: "Safety Resources", value: "Safety Resources" },
];

// const sectionOrder = [
//     "Safe Manhours",
//     "Training",
//     "Safety Meeting",
//     "Reward & Recognition",
//     "Monitoring & Measurement",
//     "Finding Tracker",
//     "Environment Reporting",
//     "Safety Resources",
// ];

const HSETracker = () => {

    const [searchTerm, setSearchTerm] = useState("");
    const createAccess = usePermission(HSE_CREATE);
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const { projectId } = useParams();

    const dispatch = useDispatch();

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(5000);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('createdDate');

    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const anchorRef = useRef(null);
    const [isAddHSE, setIsAddHSE] = useState(false);

    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ProjectId: projectId,
        SearchTerm: "",
        Section: "",
        MonthYear: "",
        ViewType: "monthly",
        //IsResponseSharedWithAuthority: null,
    });

    const [appliedFilters, setAppliedFilters] = useState(filters);

    const isViewMode = appliedFilters.MonthYear;

    const viewTypeValue = appliedFilters.ViewType || "monthly";

    const [triggerHSEList, { data: hseList, isFetching }] = useLazyGetHSEListQuery();

    const { data: hseViewTypeList, isFetching: isViewFetching } =
        useGetHSEViewTypeListQuery(
            {
                ProjectId: projectId,
                section: appliedFilters.Section,
                monthyear: appliedFilters.MonthYear,
                viewtype: appliedFilters.ViewType || "monthly",
            },
            {
                skip: !appliedFilters.MonthYear
            }
        );

    const { data: hseData } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

    useEffect(() => {
        triggerHSEList(appliedFilters);
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.SearchTerm) {
                const updatedFilters = {
                    ...filters,
                    SearchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                triggerHSEList(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handleApplyFilters = () => {
        const updatedFilters = {
            ...filters,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        setAppliedFilters(updatedFilters);

        if (!updatedFilters.MonthYear) {
            triggerHSEList(updatedFilters);
        }

        setPageNumber(1);
        handleClose();
    };

    const handleReset = () => {
        const resetFilters = {
            ProjectId: projectId,
            SortBy: sortBy,
            SortOrder: sortOrder,
            PageNumber: 1,
            PageSize: pageSize,
            SearchTerm: "",
            Section: "",
            MonthYear: "",
            ViewType: "monthly",
        };
        setFilters(resetFilters);
        setAppliedFilters(resetFilters);
        triggerHSEList(resetFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleRefresh = () => {
        const refreshedFilters = {
            ...filters,
        };

        if (!appliedFilters.MonthYear) {
            triggerHSEList(refreshedFilters);
        } else {
            setAppliedFilters(refreshedFilters);
        }
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        const updatedFilters = {
            ...filters,
            [fieldName]: selectedOption?.value ?? null,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        let updatedValue = value;

        if (name === "MonthYear" && value) {
            const [year, month] = value.split("-");
            updatedValue = `${month}-${year}`; // MM-YYYY
        }

        const updatedFilters = {
            ...filters,
            [name]: updatedValue || "",
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleViewChange = (e) => {
        const value = e.target.value;

        const updatedFilters = {
            ...filters,
            ViewType: value,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleDelete = (filterType) => {
        const updatedFilters = {
            ...filters,
            [filterType]: "",
            ViewType: "monthly",
            PageNumber: 1
        };

        setFilters(updatedFilters);
        setAppliedFilters(updatedFilters);

        if (!updatedFilters.MonthYear) {
            triggerHSEList(updatedFilters);
        }

        setPageNumber(1);
    };

    const handleAddHSE = () => {
        setIsAddHSE((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const transformHseData = (data = []) => {
        const monthFormat = "MMM YYYY";
        const monthsSet = new Set();
        const grouped = {};

        data.forEach((item) => {
            const month = dayjs(item.date).format(monthFormat);
            monthsSet.add(month);

            if (!grouped[item.section]) {
                grouped[item.section] = {};
            }

            if (!grouped[item.section][item.description]) {
                grouped[item.section][item.description] = {};
            }

            grouped[item.section][item.description][month] =
                (grouped[item.section][item.description][month] || 0) + item.value;
        });

        const months = Array.from(monthsSet).sort(
            (a, b) => dayjs(a, monthFormat) - dayjs(b, monthFormat)
        );

        return { grouped, months };
    };

    const prepareViewTypeExport = (apiData = [], viewType) => {
        const rows = [];
        const merges = [];
        let rowIndex = 1;

        const flattened = apiData.flatMap((row) =>
            row?.data?.map((item) => ({
                period: item.period,
                value: item.value,
                section: row.section,
                description: row.description,
            })) || []
        );

        const sorted = [...flattened].sort((a, b) => {
            if (a.period !== b.period) return a.period.localeCompare(b.period);
            if (a.section !== b.section) return a.section.localeCompare(b.section);
            return a.description.localeCompare(b.description);
        });

        const viewLabelMap = { monthly: "Month", weekly: "Week", daily: "Day" };
        const viewLabel = viewLabelMap[viewType] || "Period";

        let currentPeriod = null;
        let startRow = rowIndex;

        const { grouped, periods } = transformPeriodData(sorted, viewType);

        sectionOrder.filter(section => grouped[section]).forEach(section => {

            const descriptions = grouped[section];

            const startRow = rowIndex

            const dessEnteries = Object.entries(descriptions);

            dessEnteries.forEach(([description, values]) => {

                const row = {
                    Section: section,
                    Description: description
                }

                let total = 0;

                periods.forEach(period => {
                    const val = values[period] || 0;
                    row[period] = val;
                    if (val !== 0) {
                        total += val
                    }
                })

                row["Total"] = total ? total.toFixed(2) : 0;
                rows.push(row);
                rowIndex++
            })
            const endRow = rowIndex - 1;

            if (endRow > startRow) {
                merges.push({
                    s: { r: startRow, c: 0 },
                    e: { r: endRow, c: 0 }
                })
            }
        })

        // sorted.forEach((item, i) => {
        //     rows.push({
        //         [viewLabel]: formatPeriod(item.period, viewType),
        //         Section: item.section,
        //         Description: item.description,
        //         Value: item.value ?? "-",
        //     });

        //     if (currentPeriod === null) {
        //         currentPeriod = item.period;
        //         startRow = rowIndex;
        //     }

        //     if (currentPeriod !== item.period) {
        //         merges.push({
        //             s: { r: startRow, c: 0 },
        //             e: { r: rowIndex - 1, c: 0 },
        //         });

        //         currentPeriod = item.period;
        //         startRow = rowIndex;
        //     }

        //     rowIndex++;

        //     if (i === sorted.length - 1) {
        //         merges.push({
        //             s: { r: startRow, c: 0 },
        //             e: { r: rowIndex - 1, c: 0 },
        //         });
        //     }
        // });

        return { rows, merges };
    };

    const prepareMainTableExport = (data = []) => {

        const rows = [];
        const merges = [];
        let rowIndex = 1;

        const { grouped, months } = transformHseData(data);

        sectionOrder
            .filter((section) => grouped[section])
            .forEach((section) => {

                const descriptions = grouped[section];
                const descEntries = Object.entries(descriptions);

                const startRow = rowIndex;

                descEntries.forEach(([description, values]) => {

                    const row = {
                        Section: section,
                        Description: description,
                    };

                    let total = 0;

                    months.forEach((month) => {
                        const val = values[month] || 0;
                        row[month] = val;

                        if (val !== 0) total += val;
                    });

                    row["Total"] = total ? total.toFixed(2) : 0;

                    rows.push(row);

                    rowIndex++;
                });

                const endRow = rowIndex - 1;

                if (endRow > startRow) {
                    merges.push({
                        s: { r: startRow, c: 0 },
                        e: { r: endRow, c: 0 },
                    });
                }
            });

        return { rows, merges };
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const handleExportExcel = () => {
        let rows = [];
        let merges = [];

        if (isViewMode) {
            const result = prepareViewTypeExport(
                hseViewTypeList?.data?.data || [],
                viewTypeValue
            );
            rows = result.rows;
            merges = result.merges;
        } else {
            const result = prepareMainTableExport(hseList?.data || []);
            rows = result.rows;
            merges = result.merges;
        }

        if (!rows.length) {
            return;
        }

        const success = exportToExcel(rows, "HSE_Tracker_Data", "HSE Tracker", merges);
        if (success) {
            openNotification("Excel exported successfully", "success");
        }
    };

    return (
        <>

            <TrackerPageHeader title="HSE Tracker" description="Maintain HSE performance records and project safety metrics.">
                <ProjectLastUpdatedOn date={hseData?.hse} />
            </TrackerPageHeader>
            <Grid container spacing={2} sx={{ p: 2 }}>

                <Grid size={12}>
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: "2px solid #ECECEC",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            p: 0,
                            display: "flex", alignItems: "center", justifyContent: 'space-between',
                            "-webkit-overflow-scrolling": "touch",
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                justifyContent: "flex-start !important",
                                alignItems: "flex-start",
                                "& .MuiTabs-flexContainer": {
                                    justifyContent: "flex-start !important",
                                    gap: "30px",
                                },
                            }}
                        >
                            {["HSE - List"].map((label, index) => {
                                let count = 0;

                                if (label === "All") {
                                    //count = totalCount;
                                }

                                return (
                                    <Tab
                                        key={index}
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        color: tabValue === index ? "#0E73F6" : "#7E7E7E",
                                                        fontFamily: "Outfit, sans-serif"
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                {index > 0 &&
                                                    <Chip
                                                        label={count}
                                                        sx={{
                                                            height: "20px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            lineHeight: 1,
                                                            backgroundColor: "#E5E9EB",
                                                            color: "#5F5F5F",
                                                        }}
                                                    />
                                                }

                                            </Box>
                                        }
                                        sx={{
                                            textTransform: "none",
                                            minWidth: "auto",
                                            paddingBottom: "14px",
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                            borderBottom: tabValue === index ? "2px solid #0E73F6" : "2px solid transparent",
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                        {createAccess &&
                            <CustomButton
                                label="Add HSE Data"
                                startIcon={<FaPlus />}
                                color="primary"
                                onClick={handleAddHSE}
                                sx={{
                                    backgroundColor: "#2563EB",
                                    fontFamily: 'Outfit',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '100%',
                                    letterSpacing: '0px',
                                }}
                            />
                        }

                    </Box>
                </Grid>

                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >

                                {filters.Section && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Section:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Section}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Section')}
                                    />
                                )}

                                {filters.MonthYear && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.MonthYear, "MM-YYYY").format("MMM YYYY")}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('MonthYear')}
                                    />
                                )}
                            </Stack>
                        </Grid>

                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "row", xs: "column" },
                                    alignItems: { md: "center", xs: "flex-start" },
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search By Section, Description"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: { md: '400px', xs: "auto" },
                                        // width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '20px' },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />

                                <IconButton
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                >
                                    <MdOutlineFilterAlt size={24} color="#48535B" />
                                </IconButton>

                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    sx={{ zIndex: 2000 }}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                    width: 600,
                                                }}
                                            >
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        <Grid container spacing={2}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Section</Typography>
                                                                <Select
                                                                    name="Section"
                                                                    options={hseSections}
                                                                    placeholder="Select Section"
                                                                    onChange={(option) => handleSelectChange(option, "Section")}
                                                                    value={hseSections.find(option => option.value === filters.Section) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Date (Month & Year)</Typography>
                                                                <TextField
                                                                    type="month"
                                                                    name="MonthYear"
                                                                    value={
                                                                        filters.MonthYear
                                                                            ? `${filters.MonthYear.split("-")[1]}-${filters.MonthYear.split("-")[0]}`
                                                                            : ""
                                                                    }
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="month"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "5px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        alignItems: "center",
                                                                        flexWrap: "wrap",
                                                                        mt: 1,
                                                                    }}
                                                                >

                                                                    <RadioGroup
                                                                        row
                                                                        value={filters.ViewType}
                                                                        onChange={handleViewChange}
                                                                    >
                                                                        <FormControlLabel
                                                                            value="monthly"
                                                                            control={<Radio size="small" />}
                                                                            label="Month View"
                                                                        />

                                                                        <FormControlLabel
                                                                            value="weekly"
                                                                            control={<Radio size="small" />}
                                                                            label="Week View"
                                                                        />

                                                                        {/* <FormControlLabel
                                                                            value="daily"
                                                                            control={<Radio size="small" />}
                                                                            label="Day View"
                                                                        /> */}
                                                                    </RadioGroup>

                                                                    <Box>
                                                                        <Button onClick={handleReset}
                                                                            sx={{
                                                                                textTransform: "none",
                                                                                color: "#F15744",
                                                                                fontSize: "14px",
                                                                                lineHeight: "100%",
                                                                                fontFamily: "Outfit, sans-serif",
                                                                                borderRadius: "25px",
                                                                                fontWeight: 500,
                                                                                px: "20px",
                                                                                py: "15px",
                                                                            }}>
                                                                            Reset
                                                                        </Button>
                                                                        <Button
                                                                            type="button"
                                                                            onClick={handleApplyFilters}
                                                                            variant="contained"
                                                                            sx={{
                                                                                backgroundColor: "#2563EB",
                                                                                borderRadius: "25px",
                                                                                textTransform: "none",
                                                                                fontWeight: 500,
                                                                                fontSize: "14px",
                                                                                lineHeight: "100%",
                                                                                fontFamily: "Outfit, sans-serif",
                                                                                color: "#FFFFFF",
                                                                                px: "20px",
                                                                                py: "15px",
                                                                            }}
                                                                        >
                                                                            Apply
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>

                                <IconButton onClick={handleExportExcel}>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton>

                                <IconButton onClick={handleRefresh}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={12} sx={{ mb: 2 }}>

                    {isViewMode ? (
                        <HSEMonthViewDataTable apiData={hseViewTypeList?.data?.data || []} isFetching={isViewFetching} viewTypeValue={viewTypeValue} />

                    ) : (

                        <HSEDataTable apiData={hseList?.data || []} isFetching={isFetching} />
                    )}

                </Grid>
            </Grid>

            {isAddHSE && <AddHSE open={isAddHSE} handleClose={handleAddHSE} />}
        </>
    );
}

export default HSETracker;
